import { cn } from '@devkit/ui';

export interface LogoProps {
  /** Pixel size of the icon square. Default 32. */
  size?: number;
  /** Optional extra classes for the icon wrapper. */
  className?: string;
}

/**
 * DevKit brand mark — vibrant blue gradient square with a 2×2 grid of tiles.
 * The grid represents a "toolkit" / "collection" of developer utilities.
 * Modern, abstract, instantly recognizable at any size.
 */
export function Logo({ size = 32, className }: LogoProps) {
  return (
    <span
      role="img"
      aria-label="DevKit"
      className={cn(
        'inline-flex items-center justify-center rounded-xl shadow-md shadow-primary/30',
        className
      )}
      style={{ width: size, height: size }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 32 32"
        width={size}
        height={size}
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="dk-bg" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stop-color="#3B82F6" />
            <stop offset="100%" stop-color="#1E40AF" />
          </linearGradient>
        </defs>
        <rect width="32" height="32" rx="8" fill="url(#dk-bg)" />
        <g fill="#ffffff">
          <rect x="9" y="9" width="6" height="6" rx="1.6" />
          <rect x="17" y="9" width="6" height="6" rx="1.6" opacity="0.85" />
          <rect x="9" y="17" width="6" height="6" rx="1.6" opacity="0.7" />
          <rect x="17" y="17" width="6" height="6" rx="1.6" opacity="0.55" />
        </g>
      </svg>
    </span>
  );
}

export interface WordmarkProps {
  className?: string;
}

/**
 * DevKit wordmark — "Dev" + highlighted "Kit" suffix.
 * Pair with <Logo /> to form a complete brand lockup.
 */
export function Wordmark({ className }: WordmarkProps) {
  return (
    <span className={cn('text-xl font-bold text-white', className)}>
      Dev<span className="text-primary">Kit</span>
    </span>
  );
}